import { cn } from '@/lib/utils';

export interface ChatTurnAvatarProps {
  /** Whether the turn is from Anaka (assistant) or the signed-in user. */
  role: 'user' | 'assistant';
  /** Initial letter for Anaka (defaults to "A") or the user's first-name initial. */
  initial?: string;
  /** Optional avatar image URL (user-only; Anaka renders the gradient disc). */
  src?: string | null;
  className?: string;
}

/**
 * 28px avatar disc used by the chat thread, per handoff `global-chat.jsx`.
 *
 * - Assistant: `linear-gradient(135deg,#7C3AED,#3B82F6)` disc with white "A".
 *   Size 28px, `mt-0.5 shrink-0`.
 * - User: neutral disc (`var(--av-neutral)`) with the user's initial.
 *   Falls back to "U" when no initial is supplied. An `src` renders a real avatar.
 *
 * Visual-only; no data fetching. The transcript supplies the role and initial.
 */
export function ChatTurnAvatar({ role, initial, src, className }: ChatTurnAvatarProps) {
  const isAgent = role === 'assistant';
  const label = (initial ?? (isAgent ? 'A' : 'U')).slice(0, 2).toUpperCase();

  if (isAgent) {
    return (
      <span
        aria-hidden
        className={cn(
          'mt-0.5 grid size-7 shrink-0 place-items-center rounded-full text-[11px] font-semibold text-white',
          className,
        )}
        style={{ background: 'linear-gradient(135deg, #7C3AED, #3B82F6)' }}
      >
        {label}
      </span>
    );
  }

  if (src) {
    return (
      <span
        aria-hidden
        className={cn(
          'mt-0.5 size-7 shrink-0 overflow-hidden rounded-full',
          className,
        )}
        style={{ background: 'var(--av-neutral)' }}
      >
        <img
          src={src}
          alt=""
          className="size-full object-cover"
          draggable={false}
        />
      </span>
    );
  }

  return (
    <span
      aria-hidden
      className={cn(
        'mt-0.5 grid size-7 shrink-0 place-items-center rounded-full text-[10px] font-semibold text-foreground',
        className,
      )}
      style={{ background: 'var(--av-neutral, var(--muted))' }}
    >
      {label}
    </span>
  );
}
